import React, { createContext, useContext, useState, useEffect } from "react";
import { apiRequest } from "@/lib/api";

export interface User {
  _id: string;
  name: string;
  email?: string;
  phone: string;
  role: "admin" | "customer";
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: { email?: string; phone?: string; password?: string }) => Promise<User>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = async () => {
    try {
      setLoading(true);
      const currentUser = await apiRequest<User | null>("/auth/me");
      if (currentUser && currentUser.role === "admin") {
        setUser(currentUser);
      } else {
        setUser(null);
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials: { email?: string; phone?: string; password?: string }) => {
    const loggedInUser = await apiRequest<User>("/auth/login", "POST", credentials);
    if (loggedInUser.role !== "admin") {
      // Automatically trigger logout to clear any set cookie
      await apiRequest("/auth/logout", "POST");
      throw new Error("Access denied. You must be an administrator to access the dashboard.");
    }
    setUser(loggedInUser);
    return loggedInUser;
  };

  const logout = async () => {
    try {
      await apiRequest("/auth/logout", "POST");
    } catch (err) {
      console.error("Logout failed on server:", err);
    } finally {
      setUser(null);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
